import React, {useEffect, useState} from 'react';
import {
    Autocomplete,
    Box,
    Chip,
    CircularProgress,
    TextField,
    Typography,
} from '@mui/material';
import {Business as BusinessIcon} from '@mui/icons-material';
import {RTL} from '../../cache/CacheRtl';
import {getCompanyBreakdown} from '../../services/DashboatrdService';

/**
 * GroupAdminCompaniesField — a multi-select of the companies a COMPANY_GROUP_ADMIN is authorized to
 * manage, used when the group admin creates content (exercises / workout programs) that must be shared
 * with a chosen subset of those companies.
 *
 * The option list is the group admin's OWN authorized companies. The server already scopes the
 * company-breakdown endpoint to the caller's authorized set (SecurityConfig + CompanyBreakdownService),
 * so there is no way to pick a company outside that set — the backend re-validates the subset anyway.
 *
 * Emits the `companyPermissions` wire shape ([{companyId, companyName}]) the group-admin controllers
 * read, so callers can hand the value straight to the create payload.
 *
 * @param {Array<{companyId:number, companyName:string}>|null} value  currently selected companies
 * @param {(companyPermissions:Array<{companyId:number, companyName:string}>) => void} onChange
 * @param {boolean} [error]     mark the field invalid (e.g. nothing selected on submit)
 * @param {boolean} [disabled]
 * @param {string}  [label]            section label (defaults to "שיוך לחברות מורשות")
 * @param {string}  [emptyHelperText]  helper shown when the selection is valid/empty; callers pass
 *                                     content-specific copy (program vs. exercise)
 */
const DEFAULT_EMPTY_HELPER =
    'אופציונלי — ניתן להשאיר ריק וליצור את התוכנית בספרייה שלך בלבד, ולשייך חברות בהמשך בעריכה';

const GroupAdminCompaniesField = ({
    value,
    onChange,
    error = false,
    disabled = false,
    label = 'שיוך לחברות מורשות',
    emptyHelperText = DEFAULT_EMPTY_HELPER,
}) => {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(false);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setLoadError(false);
        getCompanyBreakdown()
            .then((res) => {
                if (!active) return;
                const list = (res?.data?.companies || []).map((c) => ({
                    companyId: c.id ?? c.companyId,
                    companyName: c.name ?? c.companyName,
                }));
                setCompanies(list);
            })
            .catch(() => {
                if (active) setLoadError(true);
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, []);

    const selectedIds = new Set((value || []).map((v) => v.companyId));
    const selectedOptions = companies.filter((c) => selectedIds.has(c.companyId));

    const handleChange = (_event, newValue) => {
        onChange(newValue.map((c) => ({companyId: c.companyId, companyName: c.companyName})));
    };

    const helperText = loadError
        ? 'שגיאה בטעינת החברות המורשות'
        : error
            ? 'יש לבחור לפחות חברה אחת'
            : emptyHelperText;

    return (
        <Box>
            <Typography
                sx={{
                    fontWeight: 600,
                    fontSize: '14px',
                    color: '#1E293B',
                    marginBottom: 1,
                    fontFamily: '"Heebo", "Roboto", sans-serif',
                }}
            >
                {label}
            </Typography>
            <RTL>
                <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={companies}
                    value={selectedOptions}
                    onChange={handleChange}
                    disabled={disabled || loadError}
                    loading={loading}
                    getOptionLabel={(option) => option.companyName || ''}
                    isOptionEqualToValue={(option, val) => option.companyId === val.companyId}
                    noOptionsText="אין חברות מורשות"
                    renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => {
                            const tagProps = getTagProps({index});
                            return (
                                <Chip
                                    {...tagProps}
                                    key={option.companyId}
                                    icon={<BusinessIcon sx={{fontSize: 14}}/>}
                                    label={option.companyName}
                                    size="small"
                                    sx={{
                                        backgroundColor: '#EFF6FF',
                                        color: '#1E40AF',
                                        border: '1px solid #BFDBFE',
                                    }}
                                />
                            );
                        })
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={selectedOptions.length === 0 ? 'בחר חברות…' : ''}
                            error={error || loadError}
                            helperText={helperText}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loading ? <CircularProgress color="inherit" size={18}/> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#FFFFFF',
                                    '& fieldset': {borderColor: error ? '#EF4444' : '#E2E8F0'},
                                    '&:hover fieldset': {borderColor: error ? '#EF4444' : '#CBD5E1'},
                                    '&.Mui-focused fieldset': {borderColor: error ? '#EF4444' : '#64748B'},
                                },
                            }}
                        />
                    )}
                />
            </RTL>
        </Box>
    );
};

export default GroupAdminCompaniesField;
